import React, { useState } from 'react';
import type { RefObject } from 'react';
import { useCallStore, Period } from '../store/useCallStore';
import { useAuthStore } from '../store/useAuthStore';
import { useToastStore } from '../store/useToastStore';
import FieldPicker from './FieldPicker';
import { exportToCsv } from '../utils/exportCsv';
import { fetchCallHistory, isWarming } from '../api/callhistory';
import { applyCompanyDefaults } from '../utils/applyCompanyDefaults';
import { DEFAULT_FIELDS } from '../constants/fieldDefs';
import type { Company } from '../types/auth';

const PERIODS: { value: Period; label: string }[] = [
  { value: 'TODAY', label: 'Heute' },
  { value: 'THIS_MONTH', label: 'Dieser Monat' },
  { value: 'LAST_MONTH', label: 'Letzter Monat' },
];

interface Props {
  onRefresh: () => void;
  loading: boolean;
  searchInputRef?: RefObject<HTMLInputElement | null>;
  /** Für Admins: Unternehmen für die „Als Unternehmen ansehen"-Auswahl. */
  companies?: Company[];
}

export default function FilterBar({ onRefresh, loading, searchInputRef, companies }: Props) {
  const { filter, setFilter, calls, wsConnected } = useCallStore();
  const { user, company } = useAuthStore();
  const addToast = useToastStore((s) => s.addToast);
  const [showPicker, setShowPicker] = useState(false);
  const [showCsvMenu, setShowCsvMenu] = useState(false);
  const [exporting, setExporting] = useState(false);

  const lockFields = user?.role !== 'admin' && (company?.filterConfig.lockFields ?? false);
  const allowedPeriods = (user?.role !== 'admin' && company?.filterConfig.allowedPeriods?.length)
    ? company.filterConfig.allowedPeriods
    : (PERIODS.map((p) => p.value) as Period[]);

  // Admin: Sicht eines Unternehmens einnehmen (read-only) — Filter-Vorgaben der
  // Firma werden übernommen; zurück auf „Alle Anrufe" setzt die App-Defaults.
  function switchViewAs(companyId: string) {
    if (!companyId) {
      setFilter({
        viewAsCompanyId: null,
        period: 'THIS_MONTH', count: 50, fields: DEFAULT_FIELDS, missedOnly: false,
      });
      return;
    }
    const target = companies?.find((c) => c.id === companyId) ?? null;
    setFilter({ viewAsCompanyId: companyId });
    applyCompanyDefaults(target);
  }

  // CSV über den GANZTEN Zeitraum: filterCount 0 = keine Kappung serverseitig.
  async function exportFullPeriod() {
    setShowCsvMenu(false);
    setExporting(true);
    try {
      const data = await fetchCallHistory({
        period: filter.period, count: 0, fields: filter.fields,
        missedOnly: filter.missedOnly, search: filter.search,
        viewAsCompanyId: filter.viewAsCompanyId,
      });
      if (isWarming(data)) {
        addToast('Der Zeitraum wird noch geladen — bitte gleich erneut versuchen.', 'danger');
        return;
      }
      exportToCsv(data, filter.fields);
      addToast(`${data.length.toLocaleString('de-DE')} Anrufe exportiert.`);
    } catch {
      addToast('Export fehlgeschlagen.', 'danger');
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      <div className="d-flex align-items-end gap-3 mb-3 flex-wrap filter-bar">
        {/* Admin: Ansicht wechseln */}
        {user?.role === 'admin' && companies && companies.length > 0 && (
          <div>
            <label className="form-label mb-1 small fw-semibold text-muted">Ansicht</label>
            <select
              className={`form-select form-select-sm ${filter.viewAsCompanyId ? 'border-info' : ''}`}
              style={{ maxWidth: 220 }}
              value={filter.viewAsCompanyId ?? ''}
              onChange={(e) => switchViewAs(e.target.value)}
              title="Read-only durch die Brille eines Unternehmens schauen"
            >
              <option value="">Alle Anrufe (Admin)</option>
              {companies.map((c) => <option key={c.id} value={c.id}>👁 {c.name}</option>)}
            </select>
          </div>
        )}

        {/* Zeitraum */}
        <div>
          <label className="form-label mb-1 small fw-semibold text-muted">Zeitraum</label>
          <select
            className="form-select form-select-sm"
            value={filter.period}
            onChange={(e) => setFilter({ period: e.target.value as Period })}
          >
            {PERIODS.filter((p) => allowedPeriods.includes(p.value)).map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>

        {/* Max. Anzahl */}
        <div>
          <label className="form-label mb-1 small fw-semibold text-muted">Max. Anzahl</label>
          <input
            type="number"
            className="form-control form-control-sm"
            style={{ width: 90 }}
            min={1}
            max={500}
            value={filter.count}
            onChange={(e) => setFilter({ count: Number(e.target.value) })}
          />
        </div>

        {/* Felder – hidden when company locks columns */}
        {!lockFields && (
          <div>
            <label className="form-label mb-1 small fw-semibold text-muted">Felder</label>
            <button
              className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
              onClick={() => setShowPicker(true)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                <path d="M1.5 1.5A.5.5 0 0 1 2 1h12a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.128.334L10 8.692V13.5a.5.5 0 0 1-.342.474l-3 1A.5.5 0 0 1 6 14.5V8.692L1.628 3.834A.5.5 0 0 1 1.5 3.5z"/>
              </svg>
              {filter.fields.length} Feld{filter.fields.length !== 1 ? 'er' : ''}
            </button>
          </div>
        )}

        {/* Nur verpasste */}
        <div className="d-flex flex-column">
          <label className="form-label mb-1 small fw-semibold text-muted">Anrufe</label>
          <div className="form-check form-switch mb-0 d-flex align-items-center" style={{ height: 31 }}>
            <input
              className="form-check-input me-2"
              type="checkbox"
              role="switch"
              id="missedOnly"
              checked={filter.missedOnly}
              onChange={(e) => setFilter({ missedOnly: e.target.checked })}
            />
            <label className="form-check-label small" htmlFor="missedOnly" style={{ cursor: 'pointer' }}>
              Nur verpasste
            </label>
          </div>
        </div>

        {/* Live-Status: neue Anrufe kommen per WebSocket-Push, kein Auto-Refresh nötig */}
        <div className="d-flex flex-column no-print">
          <label className="form-label mb-1 small fw-semibold text-muted">Aktualisierung</label>
          <div
            className="d-flex align-items-center gap-2 px-2 border rounded-2"
            style={{ height: 31, fontSize: '0.82rem' }}
            title={wsConnected
              ? 'Neue Anrufe erscheinen automatisch, sobald das Telefonsystem sie meldet.'
              : 'Verbindung zum Server getrennt — es wird automatisch neu verbunden.'}
          >
            <span className={wsConnected ? 'live-dot' : 'live-dot live-dot-off'} aria-hidden />
            {wsConnected ? <span className="text-success fw-semibold">Live</span> : <span className="text-muted">Verbinde…</span>}
          </div>
        </div>

        {/* Actions */}
        <div className="d-flex gap-2 ms-auto flex-wrap">
          <div className="btn-group position-relative">
            <button
              className="btn btn-outline-success btn-sm d-flex align-items-center gap-1"
              onClick={() => exportToCsv(calls, filter.fields)}
              disabled={calls.length === 0 || exporting}
              title="Angezeigte Zeilen als CSV herunterladen"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5"/>
                <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z"/>
              </svg>
              {exporting ? 'Export…' : 'CSV'}
            </button>
            <button
              className="btn btn-outline-success btn-sm dropdown-toggle dropdown-toggle-split"
              disabled={exporting}
              onClick={() => setShowCsvMenu((v) => !v)}
              aria-label="CSV-Optionen"
            />
            {showCsvMenu && (
              <ul className="dropdown-menu show" style={{ position: 'absolute', top: '100%', right: 0, zIndex: 1050 }}>
                <li>
                  <button className="dropdown-item small" onClick={() => { setShowCsvMenu(false); exportToCsv(calls, filter.fields); }} disabled={calls.length === 0}>
                    Angezeigte Zeilen ({calls.length})
                  </button>
                </li>
                <li>
                  <button className="dropdown-item small" onClick={exportFullPeriod}>
                    Gesamter Zeitraum (alle Treffer)
                  </button>
                </li>
              </ul>
            )}
          </div>

          <button
            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1 no-print"
            onClick={() => window.print()}
            title="Aktuelle Ansicht drucken"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
              <path d="M2.5 8a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1"/>
              <path d="M5 1a2 2 0 0 0-2 2v2H2a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h1v1a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-1h1a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-1V3a2 2 0 0 0-2-2zM4 3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2H4zm1 5a2 2 0 0 0-2 2v1H2a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v-1a2 2 0 0 0-2-2zm1 2a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1z"/>
            </svg>
            Drucken
          </button>

          <button
            className="btn btn-primary btn-sm d-flex align-items-center gap-1 no-print"
            onClick={onRefresh}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                Laden…
              </>
            ) : (
              'Aktualisieren'
            )}
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4 no-print">
        <input
          ref={searchInputRef}
          type="search"
          className="form-control form-control-sm"
          style={{ maxWidth: 360 }}
          placeholder="Suche nach Nummer, Name, Route… (/ zum Fokussieren)"
          value={filter.search}
          onChange={(e) => setFilter({ search: e.target.value })}
        />
      </div>

      {showPicker && (
        <FieldPicker
          onClose={() => setShowPicker(false)}
          onApply={onRefresh}
        />
      )}
    </>
  );
}
